export type Side = 'client' | 'agent';

export interface SpokenInterval {
    side: Side;
    startTime: number;
    endTime: number;
}

export interface TalkStats {
    talkSeconds: Record<Side, number>;
    ratio: Record<Side, number> | null;
    longestMonologue: {side: Side; seconds: number} | null;
    pauses: {count: number; totalSeconds: number; longestSeconds: number};
}

export const MONOLOGUE_GAP_SECONDS = 2;
export const PAUSE_MIN_SECONDS = 3;

const merge = (intervals: readonly {startTime: number; endTime: number}[]): {startTime: number; endTime: number}[] => {
    const sorted = intervals
        .filter((interval) => interval.endTime > interval.startTime)
        .map((interval) => ({startTime: interval.startTime, endTime: interval.endTime}))
        .sort((a, b) => a.startTime - b.startTime);
    const merged: {startTime: number; endTime: number}[] = [];
    for (const interval of sorted) {
        const last = merged[merged.length - 1];
        if (last && interval.startTime <= last.endTime) {
            last.endTime = Math.max(last.endTime, interval.endTime);
        } else {
            merged.push(interval);
        }
    }
    return merged;
};

const total = (intervals: readonly {startTime: number; endTime: number}[]): number =>
    intervals.reduce((sum, interval) => sum + interval.endTime - interval.startTime, 0);

const percentages = (client: number, agent: number): Record<Side, number> => {
    const clientPercent = Math.round((client / (client + agent)) * 100);
    return {client: clientPercent, agent: 100 - clientPercent};
};

const longestRun = (intervals: readonly SpokenInterval[]): {side: Side; seconds: number} | null => {
    const sorted = [...intervals]
        .filter((interval) => interval.endTime > interval.startTime)
        .sort((a, b) => a.startTime - b.startTime);
    let best: {side: Side; seconds: number} | null = null;
    let run: {side: Side; start: number; end: number} | null = null;
    for (const interval of sorted) {
        const continues = run && run.side === interval.side && interval.startTime - run.end < MONOLOGUE_GAP_SECONDS;
        if (run && continues) {
            run.end = Math.max(run.end, interval.endTime);
        } else {
            run = {side: interval.side, start: interval.startTime, end: interval.endTime};
        }
        const seconds = run.end - run.start;
        if (!best || seconds > best.seconds) best = {side: run.side, seconds};
    }
    return best;
};

export const computeTalkStats = (intervals: readonly SpokenInterval[], {voicemail = false} = {}): TalkStats => {
    const client = total(merge(intervals.filter((interval) => interval.side === 'client')));
    const agent = total(merge(intervals.filter((interval) => interval.side === 'agent')));
    const speech = merge(intervals);
    const gaps = speech.slice(1)
        .map((interval, index) => interval.startTime - speech[index].endTime)
        .filter((gap) => gap >= PAUSE_MIN_SECONDS);
    return {
        talkSeconds: {client, agent},
        ratio: voicemail || client + agent === 0 ? null : percentages(client, agent),
        longestMonologue: longestRun(intervals),
        pauses: {
            count: gaps.length,
            totalSeconds: gaps.reduce((sum, gap) => sum + gap, 0),
            longestSeconds: gaps.reduce((longest, gap) => Math.max(longest, gap), 0),
        },
    };
};
